import type { MouseEvent, ReactNode } from 'react';
import { washStyle, type TokenVar } from './wash';
import { Link } from 'react-router-dom';
import { ArrowUpRightIcon } from '@phosphor-icons/react';
import clsx from 'clsx';

// The building blocks of the home screen's cards, shared so every card has
// the same shape, wash, title row and tap target.

// Every card's surface: shape, border, wash and hover/press feedback. Layout
// (size, padding, direction) is up to the card.
const CARD_SHELL_CLASS =
  'group relative overflow-hidden text-left rounded-[22px] lg:rounded-3xl border border-border bg-elevated card-wash transition-[border-color,transform] hover:border-border-strong active:scale-[0.99]';

const CARD_CLASS = clsx(
  CARD_SHELL_CLASS,
  'flex flex-col justify-between h-[148px] lg:h-[220px] p-4 lg:p-[22px]'
);

// A tappable card surface for cards that lay out their own content, like
// Security's wide hero card.
export function CardShell({
  wash,
  ariaLabel,
  onClick,
  className,
  children,
}: {
  wash: TokenVar;
  ariaLabel: string;
  onClick: (e: MouseEvent<HTMLButtonElement>) => void;
  className?: string;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      aria-label={ariaLabel}
      onClick={onClick}
      className={clsx(CARD_SHELL_CLASS, className)}
      style={washStyle(wash)}
    >
      {children}
    </button>
  );
}

interface HomeCardProps {
  title: string;
  wash: TokenVar;
  ariaLabel: string;
  // Navigate somewhere, or open an overlay (given the card's rect to grow from).
  to?: string;
  onOpen?: (rect: DOMRect) => void;
  // Absolutely positioned graphics behind the content (charts, gauges).
  decoration?: ReactNode;
  // The bottom of the card: the big number and its caption.
  children: ReactNode;
}

// One of the smaller cards in the grid: title and open-arrow along the top,
// content along the bottom, an optional graphic behind both.
export function HomeCard({
  title,
  wash,
  ariaLabel,
  to,
  onOpen,
  decoration,
  children,
}: HomeCardProps) {
  const body = (
    <>
      {decoration}
      <span className="relative flex items-center justify-between gap-2">
        <span className="text-base lg:text-lg font-semibold">{title}</span>
        <ArrowUpRightIcon
          size={18}
          weight="bold"
          className="shrink-0 text-fg-secondary group-hover:text-fg transition-colors"
          aria-hidden
        />
      </span>
      {children}
    </>
  );

  if (to) {
    return (
      <Link to={to} aria-label={ariaLabel} className={CARD_CLASS} style={washStyle(wash)}>
        {body}
      </Link>
    );
  }
  return (
    <CardShell
      wash={wash}
      ariaLabel={ariaLabel}
      onClick={(e) => onOpen?.(e.currentTarget.getBoundingClientRect())}
      className="flex flex-col justify-between h-[148px] lg:h-[220px] p-4 lg:p-[22px]"
    >
      {body}
    </CardShell>
  );
}

// The grid's empty slot, held for the next family app. Decorative only.
export function ComingSoonCard() {
  return (
    <div className="h-[148px] lg:h-[220px] rounded-[22px] lg:rounded-3xl border border-dashed border-border-strong grid place-items-center">
      <span className="text-sm text-fg-faint">Coming soon</span>
    </div>
  );
}

// The big white number at the bottom of a card, with its caption beneath.
// `unit` ($ or °) is set smaller and grey so the digits carry the weight.
export function CardFigure({
  value,
  unit,
  unitPosition = 'before',
  caption,
}: {
  value: string;
  unit?: string;
  unitPosition?: 'before' | 'after';
  caption: string;
}) {
  const unitEl = unit ? (
    <span className="text-[0.6em] font-semibold text-fg-secondary align-[0.42em] tracking-normal">
      {unit}
    </span>
  ) : null;
  return (
    <span className="relative flex flex-col gap-0.5">
      <span className="tabular text-[28px] lg:text-[32px] leading-tight font-bold tracking-[-0.03em]">
        {unitPosition === 'before' && unitEl}
        {value}
        {unitPosition === 'after' && unitEl}
      </span>
      <span className="text-xs lg:text-sm text-fg-secondary">{caption}</span>
    </span>
  );
}

// A small washed tile inside a card — the security statuses and the calendar
// counts. Same treatment as the kiosk cards, one size down.
export function StatusTile({
  wash,
  className,
  children,
}: {
  wash: TokenVar;
  className?: string;
  children: ReactNode;
}) {
  return (
    <span
      className={clsx(
        'flex flex-col rounded-2xl lg:rounded-[20px] border border-border bg-overlay card-wash',
        className
      )}
      style={washStyle(wash)}
    >
      {children}
    </span>
  );
}
